//#region libraries
// react
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

// custom components
import { PercentageDifferenceRow, CircleDigit } from "_molecules";

// redux
import { selectTravelAgent } from "_redux/slices/main-screen/user";
import {
  selectCountInbound,
  selectCountInboundForecast,
  selectCountOutboundForecast,
  selectCountOutbound,
  selectGetBookingForecastOutboundStatus,
  selectGetBookingForecastInboundStatus,
  selectGetBookingOutboundStatus,
  selectGetBookingInboundStatus
} from "_redux/slices/prm-bookings/bookings-count";
import { setDate, selectSeasonalVarianceFilter } from "_redux/slices/prm-bookings/calendar";
import { selectUserDb } from "_redux/slices/main-screen/user";
import { selectSeasonalVariances } from "_redux/slices/prm-bookings/seasonal-variance";

// public libraries
import { format } from "date-fns";

// styles
import { Colors, Spacing } from "_styles";
import { Subtitle, Text, Bold } from "@tremor/react";

// utils

import { isPastDate, isToday } from '_utils/date-time-functions'

//#endregion

function getColorSpectrum(index) {
  /* busy day index
        -1 : normal day
        0  : least busy day
        1  : second least busy
        5  : second most busy day 
        6  : busiest day 
    */

  let mapping = {
    6: "#DE3700",
    5: "#F58B00",
    1: "#3cd668",
    0: "#2AA10F",
  };

  return mapping[index] || "#187CFA";
}

const CalendarTile = (props) => {

  // nav
  const history = useHistory();

  //#region variables
  const today = format(new Date(), "yyyy-MM-dd");
  // Build date string directly without Date parsing to avoid timezone issues
  const month = String(props?.month + 1).padStart(2, "0");
  const day = String(props?.date).padStart(2, "0");
  const dayOfTile = `${props?.year}-${month}-${day}`;

  const monthNumber = props.month + 1;

  const tileWidth = Spacing.SCALE_180;
  const tileHeight = Spacing.SCALE_130;

  //#endregion

  //#region states

  // percentage difference

  const [total, setTotal] = useState(0);                      // total count of actual bookings
  const [forecastTotal, setForecastTotal] = useState(0);      // total count of forecasted bookings
  const [outboundForecast, setOutboundForecast] = useState(0);      // total count of forecasted bookings
  const [inboundForecast, setInboundForecast] = useState(0);      // total count of forecasted bookings


  // hover / tile border states
  const [toggle, setToggle] = useState(false);
  const [border, setBorder] = useState(false);
  const [outside, setOutside] = useState(false);
  const [notInMonth, setNotInMonth] = useState(false);
  const [inPast, setInPast] = useState(false);
  const [todaysDate, setTodaysDate] = useState(false);

  const [tileDateFormatted, setTileDateFormatted] = useState('');
  //#endregion

  //#region redux 
  const dispatch = useDispatch();
  // user
  const dbUser = useSelector(selectUserDb);
  // counts
  const bookingsCountInbound = useSelector(selectCountInbound);
  const bookingsCountOutbound = useSelector(selectCountOutbound);

  // forecasts
  const bookingsCountInboundForecast = useSelector(selectCountInboundForecast);
  const bookingsCountOutboundForecast = useSelector(selectCountOutboundForecast);

  // seaonsal variances
  const seasonalVariances = useSelector(selectSeasonalVariances);
  const seasonalVarianceFilter = useSelector(selectSeasonalVarianceFilter);

  // status flags
  const bookingForecastInboundStatus = useSelector(selectGetBookingForecastInboundStatus);
  const bookingForecastOutboundStatus = useSelector(selectGetBookingForecastOutboundStatus);
  const bookingInboundStatus = useSelector(selectGetBookingInboundStatus);
  const bookingOutboundStatus = useSelector(selectGetBookingOutboundStatus);

  // Highlighting by busy-ness
  const [busyDayIndex, setBusyDayIndex] = useState(-1);
  const [leastBusyDay, setLeastBusyDay] = useState(false);
  const [busiestDay, setBusiestDay] = useState(false);

  const travelAgent = useSelector(selectTravelAgent);

  //#endregion

  //#region functions
  // handle click on tile
  const handleClick = () => {
    if (outside) { return }       // tile is outside of current month so do nothing

    // set date of chosen tile
    dispatch(setDate(dayOfTile));

    if (travelAgent) {
      // go to booking history for travel agent
      history.push("/booking-history");
    } else {
      // switch to booking list screen
      history.push("/prm-bookings");
    }
  };

  //#endregion

  //#region use effects

  // check if calendar tile is within any seasonal variances
  useEffect(() => {
    // check each variance

    if (seasonalVariances && seasonalVariances.seasonalAdjustments && seasonalVarianceFilter) {

      const tempTileDate = new Date(dayOfTile);

      const currentMonthSeasonalVariances = seasonalVariances.seasonalAdjustments.filter(seasonalVariance => (tempTileDate >= new Date(seasonalVariance.startDate) && tempTileDate <= new Date(seasonalVariance.endDate)))
      if (currentMonthSeasonalVariances.length > 0) {
        currentMonthSeasonalVariances.forEach((variance) => {
          // console.log('adding variance to forecast of ' + variance.multiplier + ' on ' + dayOfTile)

          // add variance on to forecast
          let tempOutbound = bookingsCountOutboundForecast && bookingsCountOutboundForecast[dayOfTile] ? Math.round(bookingsCountOutboundForecast[dayOfTile]) : 0
          let adjustedOutbound = tempOutbound + (tempOutbound * variance.multiplier)
          Math.round(adjustedOutbound)
          setOutboundForecast(Math.round(adjustedOutbound))

          let tempInbound = bookingsCountInboundForecast && bookingsCountInboundForecast[dayOfTile] ? Math.round(bookingsCountInboundForecast[dayOfTile]) : 0
          let adjustedInbound = tempInbound + (tempInbound * variance.multiplier)
          setInboundForecast(Math.round(adjustedInbound))
          // set total
          setForecastTotal(Math.round(adjustedOutbound) + Math.round(adjustedInbound))

        })
      } else {
        const departureForecast = bookingsCountOutboundForecast && bookingsCountOutboundForecast[dayOfTile] ? Math.round(bookingsCountOutboundForecast[dayOfTile]) : 0;
        const arrivalForecast = bookingsCountInboundForecast && bookingsCountInboundForecast[dayOfTile] ? Math.round(bookingsCountInboundForecast[dayOfTile]) : 0;
        setForecastTotal(arrivalForecast + departureForecast)
        setOutboundForecast(departureForecast)
        setInboundForecast(arrivalForecast)
      }
    }
    else {
      const departureForecast = bookingsCountOutboundForecast && bookingsCountOutboundForecast[dayOfTile] ? Math.round(bookingsCountOutboundForecast[dayOfTile]) : 0;
      const arrivalForecast = bookingsCountInboundForecast && bookingsCountInboundForecast[dayOfTile] ? Math.round(bookingsCountInboundForecast[dayOfTile]) : 0;
      setForecastTotal(arrivalForecast + departureForecast)
      setOutboundForecast(departureForecast)
      setInboundForecast(arrivalForecast)
    }
  }, [seasonalVarianceFilter, seasonalVariances, bookingsCountOutboundForecast, bookingsCountInboundForecast, dayOfTile]);

  // calculate least and most busy dat
  useEffect(() => {
    if (props.leastAndHighestDay) {
      if (!notInMonth && props.leastAndHighestDay) {
        for (let i in Object.keys(props.leastAndHighestDay)) {
          if (
            props.leastAndHighestDay[i] &&
            dayOfTile == props.leastAndHighestDay[i].mostBusyDay
          ) {
            setBusiestDay(true);
          } else {
            // setBusiestDay(false)
          }

          if (
            props.leastAndHighestDay[i] &&
            dayOfTile == props.leastAndHighestDay[i].leastBusyDay
          ) {
            setLeastBusyDay(true);
          } else {
            // setLeastBusyDay(false)
          }
        }
      }
    }
    if (notInMonth) {
      setLeastBusyDay(false);
      setBusiestDay(false);
    }
  }, [props.leastAndHighestDay, notInMonth]);

  useEffect(() => {
    if (
      props.orderedDays != null &&
      props.orderedDays != undefined &&
      Object.keys(props.orderedDays)
    ) {
      // find index of week that date is in
      let weekIndex = Object.keys(props.orderedDays).filter((key) =>
        Object.keys(props.orderedDays[key]).includes(dayOfTile)
      )[0];

      if (weekIndex) {
        // get forecasted count for week of date
        let dateBookingsMapping = props.orderedDays[weekIndex];



        // if date in month
        if (!notInMonth) {
          // sort values of week to remove nan values (from days not in month)
          let val = Object.keys(dateBookingsMapping)
            .filter(
              (k) =>
                !isNaN(dateBookingsMapping[k]) &&
                !(dateBookingsMapping[k] == null)
            )
            .sort((i, j) => dateBookingsMapping[i] - dateBookingsMapping[j]);

          // index value of date representing how busy it
          let index = val.indexOf(dayOfTile);

          /* busy day index
                    -1 : normal day
                    0  : least busy day
                    1  : second least busy
                    5  : second most busy day 
                    6  : busiest day 
                */

          switch (index) {
            case 0:
              setBusyDayIndex(0);
              break;

            case 1:
              setBusyDayIndex(1);
              break;

            case val.length - 1:
              setBusyDayIndex(6);
              break;

            case val.length - 2:
              setBusyDayIndex(5);
              break;
            default:
              setBusyDayIndex(-1);
              break;
          }
        } else {
          setBusyDayIndex(-1);
        }
      }
    }
  }, [props.orderedDays, notInMonth]);

  // set active border if todays date = tile date
  useEffect(() => {
    if (today == dayOfTile) {
      setBorder(true);
    }
  }, []);

  // set tile clickable or non clickable
  useEffect(() => {
    // Use the date string directly to avoid timezone issues
    const tileDate = dayOfTile;
    setTileDateFormatted(tileDate)

    if (props.calendarMonth != monthNumber) {
      setNotInMonth(true);
    } else {
      setNotInMonth(false);
    }

    // check if in past
    if (isPastDate(tileDate)) {
      setInPast(true);
    }
    // check if current date
    if (isToday(tileDate)) {
      setTodaysDate(true);
    }
  }, [props, dayOfTile]);

  // calculate total
  useEffect(() => {
    const departuresCount = bookingsCountOutbound && bookingsCountOutbound[dayOfTile] ? bookingsCountOutbound[dayOfTile] : 0;
    const arrivalsCount = bookingsCountInbound && bookingsCountInbound[dayOfTile] ? bookingsCountInbound[dayOfTile] : 0;
    setTotal(departuresCount + arrivalsCount)
  }, [bookingsCountOutbound, bookingsCountInbound]);

  //#endregion

  return (
    <div
      style={{
        opacity: outside || inPast || notInMonth ? 0.5 : 1.0,
        cursor: !outside ? "pointer" : null,
        borderStyle: notInMonth ? 'none' : "solid",
        borderColor: !notInMonth ? Colors.PRM_LIGHT_BLUE : "#187CFA",
        width: tileWidth,
        height: tileHeight,
        borderWidth: toggle || (todaysDate && !outside) ? 2.5 : 1.2
      }}
      onClick={handleClick}
      onMouseEnter={() => { setToggle(true) }}
      onMouseLeave={() => { setToggle(false); }}
    >
      {!notInMonth &&
        <div style={{ marginLeft: Spacing.SCALE_8 }}>

          {/* date */}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>

            {/* date */}
            <div style={{ display: "flex" }}>
              {border ? <Text><Bold>{props.date}</Bold></Text> : <Subtitle>{props.date}</Subtitle>}
            </div>

            <div style={{ minHeight: 14, maxHeight: 14, marginLeft: Spacing.SCALE_3, borderWidth: 0.001, borderStyle: 'solid', borderColor: 'white', marginTop: Spacing.SCALE_12, marginRight: Spacing.SCALE_3 }}>
              {/* Busy Day Word */}
              <div style={{ display: 'flex' }}>
                {travelAgent ? "" :
                  <>
                    {notInMonth ? ' ' : busyDayIndex == 0 || busyDayIndex == 1 || busyDayIndex == 2 || busyDayIndex == 5 || busyDayIndex == 6 ?
                      <div style={{
                        width: 10, height: 10, backgroundColor: getColorSpectrum(busyDayIndex), marginTop: -Spacing.SCALE_4, borderRadius: 25
                      }} /> : ''
                    }
                    <p
                      style={{ fontSize: 12, fontWeight: '500', color: Colors.GRAY_DARK, marginTop: -Spacing.SCALE_8, marginLeft: Spacing.SCALE_4 }}
                    >
                      {
                        notInMonth ? '' : busyDayIndex == 0 ? 'Lowest Bookings' : busyDayIndex == 1 ? '2nd Lowest Bookings' : busyDayIndex == 2 ? '2nd Highest Bookings' : busyDayIndex == 5 ? '2nd Highest Bookings' : busyDayIndex == 6 ? 'Highest Bookings' : ''
                      }
                    </p>
                  </>
                }
              </div>
            </div>
          </div>

          <div style={{ marginLeft: Spacing.SCALE_12 }}>
            {dbUser.details.type !== "AIRLINE" &&
              <PercentageDifferenceRow
                isPastDate={inPast}
                outboundCount={bookingsCountOutbound && bookingsCountOutbound[dayOfTile] ? bookingsCountOutbound[dayOfTile] : 0}
                outboundForecastCount={outboundForecast}
                outboundLoading={bookingForecastOutboundStatus == 'loading' || bookingOutboundStatus == 'loading'}

                inboundCount={bookingsCountInbound && bookingsCountInbound[dayOfTile] ? bookingsCountInbound[dayOfTile] : 0}
                inboundForecastCount={inboundForecast}
                inboundLoading={bookingForecastInboundStatus == 'loading' || bookingInboundStatus == 'loading'}

              />}


            {/* counts + totals */}
            <div style={{ display: 'flex', marginTop: Spacing.SCALE_4 }}>

              {/* counts */}
              <div>

                {/* forecast for not airline accounts */}
                {
                  dbUser.details.type !== "AIRLINE" &&
                  <div style={{ display: "flex", justifyContent: "left" }}>

                    {/* outbound forecast count */}
                    <CircleDigit
                      number={outboundForecast}
                      toggle={toggle}
                      color={"white"}
                      textColor={Colors.PRM_LIGHT_BLUE}
                      type="outbound"
                      forecast={true}
                      isLoading={bookingForecastOutboundStatus == 'loading'}
                    />

                    {/* inbound forecast count */}
                    <CircleDigit
                      number={inboundForecast}
                      toggle={toggle}
                      textColor={"green"}
                      color={"white"}
                      type="inbound"
                      style={{ marginLeft: 6 }}
                      margin={true}
                      forecast={true}
                      isLoading={bookingForecastInboundStatus == 'loading'}
                    />

                    {/* total forecasts */}
                    <CircleDigit
                      number={forecastTotal}
                      textColor={"gray"}
                      color={'white'}
                      isLoading={bookingForecastOutboundStatus == 'loading' || bookingOutboundStatus == 'loading'}
                      margin={true}
                    />
                  </div>
                }

                {/* actuals */}
                <div style={{ display: "flex", justifyContent: "left", marginTop: Spacing.SCALE_4 }}>

                  {/* outbound actual count */}
                  <CircleDigit
                    number={bookingsCountOutbound && bookingsCountOutbound[dayOfTile] ? bookingsCountOutbound[dayOfTile] : 0}
                    color={Colors.PRM_LIGHT_BLUE}
                    textColor={"white"}
                    type="outbound"
                    inverted={true}
                    isLoading={bookingOutboundStatus == 'loading'}
                  />

                  {/* inbound actual count */}
                  <CircleDigit
                    number={bookingsCountInbound && bookingsCountInbound[dayOfTile] ? bookingsCountInbound[dayOfTile] : 0}
                    color={"green"}
                    textColor={"white"}
                    type="inbound"
                    margin={true}
                    isLoading={bookingInboundStatus == 'loading'}
                  />

                  {/* total actuals */}
                  <CircleDigit
                    number={total}
                    color={"white"}
                    textColor={Colors.PRM_LIGHT_BLUE}
                    isLoading={bookingForecastInboundStatus == 'loading' || bookingInboundStatus == 'loading'}
                    margin={true}
                  />
                </div>
              </div>

              {/* totals */}
              <div style={{ marginLeft: Spacing.SCALE_12 }}>



              </div>
            </div>

          </div>

        </div>
      }

    </div>
  );
};

export default CalendarTile;
